/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
    IContextualMenuStyleProps,
    IContextualMenuStyles
} from '@fluentui/react/lib/ContextualMenu';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { handleMenuListRender } from 'src/luis/modules/labeling/components/handleMenuListRender';
import { EntityDropdownProps } from 'src/luis/modules/labeling/types/EntityDropdownProps';
import { getMenuActionItems } from 'src/luis/modules/labeling/utils/entityDropdown/getMenuActionItems';
import { getNotFoundItem } from 'src/luis/modules/labeling/utils/entityDropdown/getNotFoundItem';
import { MenuActionItems, menuActionsGenerator } from 'src/luis/modules/labeling/utils/entityDropdown/menuActionsGenerator';
import { menuEntitiesGenerator } from 'src/luis/modules/labeling/utils/entityDropdown/menuEntitiesGenerator';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { entityDropDownFocusId } from 'src/luis/projects/utterances/components/UtteranceTable/IntentPageUtteranceRow';

const messages = defineMessages({
    subNodeSearchPlaceholder: {
        id: 'labeling.entityDropdown.subNodeSearchPlaceholder',
        defaultMessage: 'Search/create a new subnode'
    },
    listEntitySearchPlaceholder: {
        id: 'labeling.entityDropdown.listEntitySearchPlaceholder',
        defaultMessage: 'Search/create a normalized value'
    },
    components: {
        id: 'labeling.entityDropdown.components',
        defaultMessage: 'components'
    },
    createNewEntity: {
        id: 'labeling.entityDropdown.createNewEntity',
        defaultMessage: 'Create new entity'
    },
    filterEntities: {
        id: 'labeling.entityDropdown.filterEntities',
        defaultMessage: 'Filter entities'
    },
    entitySearchPlaceholder: {
        id: 'labeling.entityDropdown.entitySearchPlaceholder',
        defaultMessage: 'Enter an entity name'
    },
    announcedLabeledEntity: {
        id: 'labeling.entityDropdown.announcedLabeledEntity',
        defaultMessage: '{entityName} label added'
    },
    announcedUnLabeledEntity: {
        id: 'labeling.entityDropdown.announcedUnLabeledEntity',
        defaultMessage: '{entityName} label removed'
    },
    announcedUnLabeledEntities: {
        id: 'labeling.entityDropdown.announcedUnLabeledEntities',
        defaultMessage: 'Menu labels removed'
    }
});

export const menuWidth = 270;

const theme = getScopedTheme('EntityDropdown');
const dropdownStyles = classNamesFunction<IContextualMenuStyleProps, IContextualMenuStyles>()({
    root: {
        boxShadow: theme.menuBoxShadow,
        minWidth: menuWidth
    },
    header: {
        fontSize: '14px'
    }
});
const callOutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    root: { marginTop: '7px' }
});

/**
 * The Component responsible for rendering multiple options for the user upon selecting a token.
 *
 * @param props
 */
export const EntityDropdown = React.memo((props: EntityDropdownProps) => {
    const {
        entitySelection,
        havePredictions,
        entities,
        target,
        onSubmit,
        openEntityPane,
        openPrebuiltEntityModal,
        openPatternsPage,
        confirmPrediction,
        onSubmitSublist,
        onCreate,
        onUnLabel,
        hidden,
        onDismiss,
        onCreateSublist,
        onCreateChild,
        isEntityPaneOpen,
        config: { enableEntityPaneAction = true, enablePrebuiltModalAction = true } = {
            enableEntityPaneAction: true,
            enablePrebuiltModalAction: true
        }
    } = props;

    const { localize } = useLocalization();
    const clickedOnLabel = entitySelection?.clickedOnLabel;
    const [announcedMessage, setAnnouncedMessage] = useState('');

    const actions: MenuActionItems = menuActionsGenerator({
        callbackFunctions: {
            openEntityPane,
            openPatternsPage,
            confirmPrediction,
            onUnLabel,
            openPrebuiltEntityModal
        },
        localize,
        clickedOnLabel,
        isEntityPaneOpen
    });

    const generatedEntities: IContextualMenuItem[] = menuEntitiesGenerator({
        callbackFunctions: {
            onSubmit: params => {
                setAnnouncedMessage(localize(messages.announcedLabeledEntity, { entityName: params.name }));
                onSubmit(params);
            },
            onSubmitSublist,
            onUnLabel: params => {
                const announcedMessaged = params.length === 1
                        ? localize(messages.announcedUnLabeledEntity, { entityName: params[0].name })
                        : localize(messages.announcedUnLabeledEntities);
                setAnnouncedMessage(announcedMessaged);
                onUnLabel(params);
            },
            onCreateSublist,
            onCreateChild,
            onDismiss
        },
        localize,
        entities,
        entitySelection,
        actions,
        clickedOnLabel
    });

    const labeledEntities = generatedEntities
        .map(item => item.data as Entity)
        .filter(
            entity => entitySelection
                && entitySelection.kind === 'label'
                && entitySelection.selection.some(selectedEntity => entity.id === selectedEntity.id && isMachineLearned(entity.typeId))
        );

    const items: IContextualMenuItem[] = getMenuActionItems({
        generatedEntities,
        actions,
        entitySelection,
        havePredictions,
        labeledEntities,
        config: {
            enableEntityPaneAction,
            enablePrebuiltModalAction
        }
    });

    const isSelectedEntityPrediction = entitySelection?.kind === 'prediction';
    const getQueryNoMatchMenuItem = (text: string) => getNotFoundItem({
            text,
            onClick: (_, item) => {
                onCreate(item.text);
            },
            secondaryText: localize(messages.createNewEntity)
        });

    return (
        <>
            <Announced message={announcedMessage} />
            <ContextualMenu
                id={entityDropDownFocusId}
                calloutProps={{
                    // Solves an issue with Fabric component callout position calculations
                    // when expanding the content dynamically so that callout can adjust its position.
                    finalHeight: 1,
                    preventDismissOnScroll: true,
                    styles: callOutStyles
                }}
                shouldFocusOnMount={false}
                hidden={hidden}
                alignTargetEdge
                styles={dropdownStyles}
                target={target}
                items={items}
                onRenderMenuList={(renderProps, defaultRender) => handleMenuListRender({
                        defaultRender,
                        onSubmit: onCreate,
                        getQueryNoMatchMenuItem,
                        props: renderProps,
                        searchPlaceholder: localize(messages.entitySearchPlaceholder),
                        actionsOnlyMenu: isSelectedEntityPrediction,
                        isContextualMenuHidden: hidden
                    })
                }
                directionalHint={isSelectedEntityPrediction ? DirectionalHint.rightTopEdge : DirectionalHint.bottomRightEdge}
                onDismiss={onDismiss}
            />
        </>
    );
});

EntityDropdown.displayName = 'EntityDropdown';
